/**
 * R16 Ticket detail — status header, info card, description, vertical
 * timeline (status changes + comments), bottom comment input.
 * Wired: GET /api/helpdesk/<id>, POST /api/helpdesk/<id>/comments (optimistic,
 * reverts on failure). Port of design/source/screens-helpdesk.jsx
 * TicketDetailScreen.
 */
import { useLocalSearchParams } from 'expo-router';
import React from 'react';
import { StyleSheet, View } from 'react-native';

import { space } from '@bero/tokens';
import {
  backOr, CardSkeletons, CategoryChip, fmtStamp, KVRow, ticketPillStatus, TimelineRow,
} from '@/components/money/bits';
import { addComment, errorMessage, fetchTicket, useAsync } from '@/components/money/api';
import { Icon } from '@/components/money/icons';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { AppText, EmptyState, IconButton, Input, Pill, Screen, TopBar } from '@/ui';

const OPEN_STATUSES = new Set(['OPEN', 'IN_PROGRESS', 'URGENT']);

export default function TicketDetailScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { data: ticket, loading, error, refetch, mutate } = useAsync(() => fetchTicket(String(id)), [id]);

  const [comment, setComment] = React.useState('');
  const [posting, setPosting] = React.useState(false);
  const [postError, setPostError] = React.useState<string | null>(null);

  const submitComment = async () => {
    const body = comment.trim();
    if (!body || posting || !ticket) return;
    setPosting(true);
    setPostError(null);
    const optimisticId = -Date.now();
    mutate((prev) => (prev ? {
      ...prev,
      timeline: [...prev.timeline, {
        id: optimisticId, kind: 'COMMENT', status: null, title: null, body,
        author: t('hd_you'), createdAt: new Date().toISOString(),
      }],
    } : prev));
    setComment('');
    try {
      const real = await addComment(ticket.id, body);
      mutate((prev) => (prev ? { ...prev, timeline: prev.timeline.map((u) => (u.id === optimisticId ? real : u)) } : prev));
    } catch (e) {
      mutate((prev) => (prev ? { ...prev, timeline: prev.timeline.filter((u) => u.id !== optimisticId) } : prev));
      setComment(body);
      setPostError(errorMessage(e, t('hd_comment_error')));
    } finally {
      setPosting(false);
    }
  };

  return (
    <Screen scroll={false}>
      <TopBar title={t('hd_details')} onBack={backOr('/(tabs)/home')} />
      {loading ? (
        <Screen padded style={{ paddingTop: space.s4 }}>
          <CardSkeletons count={2} />
        </Screen>
      ) : error || !ticket ? (
        <Screen padded>
          <EmptyState
            title={t('hd_detail_err_title')}
            body={t('hd_detail_err_body')}
            cta={t('retry')}
            onCta={() => void refetch()}
          />
        </Screen>
      ) : (
        <>
          <Screen padded style={{ paddingTop: space.s4 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
              <CategoryChip category={ticket.category} />
              <Pill status={ticketPillStatus(ticket.status)} />
            </View>
            <AppText variant="headingLg" weight="500" style={{ marginTop: space.s2 }}>{ticket.title}</AppText>
            <AppText variant="bodySm" tone="tertiary" mono style={{ marginTop: 4 }}>#{ticket.ticketNo}</AppText>

            <View style={{ backgroundColor: colors.canvas1, borderRadius: 12, padding: space.s3, marginTop: space.s5 }}>
              <KVRow k={t('hd_raised_by')} v={ticket.raisedBy ?? t('hd_you')} />
              <KVRow k={t('hd_raised_on')} v={fmtStamp(ticket.createdAt)} />
              <KVRow k={t('hd_location')} v={ticket.location === 'COMMON_AREA' ? t('hd_loc_common') : t('hd_loc_personal')} />
              <KVRow k={t('hd_assigned_to')} v={ticket.assignee ?? t('hd_not_assigned')} />
              <KVRow k={t('hd_expected_by')} v={ticket.eta ?? t('hd_tbc')} />
            </View>

            {ticket.description ? (
              <>
                <AppText variant="label" tone="tertiary" style={{ marginTop: space.s6, marginBottom: space.s2 }}>{t('hd_description')}</AppText>
                <AppText variant="bodyLg">{ticket.description}</AppText>
              </>
            ) : null}

            <AppText variant="label" tone="tertiary" style={{ marginTop: space.s8, marginBottom: space.s3 }}>{t('hd_timeline')}</AppText>
            <View>
              {ticket.timeline.map((u, i) => {
                const isLast = i === ticket.timeline.length - 1 && !OPEN_STATUSES.has(ticket.status);
                if (u.kind === 'STATUS') {
                  return (
                    <TimelineRow
                      key={u.id}
                      title={u.title ?? t('hd_status_updated')}
                      time={fmtStamp(u.createdAt)}
                      note={u.body || undefined}
                      done
                      isLast={isLast}
                    />
                  );
                }
                return (
                  <TimelineRow
                    key={u.id}
                    title={t('hd_commented', { author: u.author })}
                    time={fmtStamp(u.createdAt)}
                    note={u.body}
                    done
                    isLast={isLast}
                  />
                );
              })}
              {OPEN_STATUSES.has(ticket.status) ? (
                <TimelineRow title={t('hd_awaiting')} time={t('hd_pending')} done={false} isLast />
              ) : null}
            </View>
            {postError ? <AppText variant="bodySm" tone="danger" style={{ marginTop: space.s2 }}>{postError}</AppText> : null}
          </Screen>

          <View style={{
            flexDirection: 'row', alignItems: 'center', gap: space.s2,
            paddingHorizontal: space.s3, paddingTop: 10, paddingBottom: 14,
            borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: colors.borderSoft,
          }}>
            <IconButton label={t('hd_attach_photo')}>
              <Icon name="camera" size={20} color={colors.textSecondary} />
            </IconButton>
            <Input
              value={comment}
              onChangeText={setComment}
              placeholder={t('hd_add_comment')}
              style={{ flex: 1, backgroundColor: colors.canvas1 }}
              onSubmitEditing={() => void submitComment()}
            />
            <IconButton label={t('hd_send_comment')} onPress={() => void submitComment()}>
              <Icon name="send" size={20} color={comment.trim() && !posting ? colors.primary500 : colors.textTertiary} />
            </IconButton>
          </View>
        </>
      )}
    </Screen>
  );
}
